/**
 * 台本取り込み (docs/features/scenario-script-import-plan.md) のフロント側の型と純粋関数。
 *
 * - 生成前: ScenarioSetupContext の importedScript (読み込んだ台本。永続化しない)
 * - 生成ペイロード: imported_script を付け、count=1 固定・dialect を送らない (§6.2)
 * - 生成後: generation_metadata.origin / imported_script / import_report の読み出しと表示用の判定
 */
import type {
  ImportReport,
  ImportedScriptMeta,
  ImportVerification,
  ScenarioOrigin,
} from '../../../features/scenarios/types';

/** 解析 API が返した台本 (FE state)。text は BE の抽出結果をそのまま保持し、FE で改変しない (§8: 行番号の基準)。 */
export interface ImportedScriptState {
  text: string;
  filename: string;
  sourceFormat: 'markdown' | 'text' | 'docx';
  charCount: number;
  warnings: string[];
}

/** 受け付ける拡張子 (.docx は BE の python-docx で変換する。計画 §8)。 */
export const SCRIPT_IMPORT_ACCEPT_EXTENSIONS = ['.docx', '.md', '.txt'] as const;
/** 受け付ける MIME。OS によって .md の MIME が空や text/x-markdown になるため拡張子判定と OR で見る。 */
export const SCRIPT_IMPORT_ACCEPT_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/markdown',
  'text/x-markdown',
  'text/plain',
] as const;
/** 解析 API と同じ上限 (超過は BE が 400 を返すが、明らかな超過は送る前に止める)。 */
export const SCRIPT_IMPORT_MAX_FILE_BYTES = 5 * 1024 * 1024;

/** 取り込み中に台本側で決まる項目の表示ラベル (§4.2)。 */
export const SCRIPT_FOLLOW_LABEL = '台本に従う';
/** ナレーション / セリフ / テロップの有無は台本の内容から決まる (表示は同じ「台本に従う」)。 */
export const SCRIPT_FOLLOW_PENDING_LABEL = '台本に従う';
/** 取り込み中に非活性にする項目のツールチップ。 */
export const SCRIPT_IMPORT_FIELD_REASON = '台本を取り込むときは台本の内容に従います';
export const SCRIPT_IMPORT_COUNT_REASON = '台本を取り込むときはシナリオを1案で作成します';
export const SCRIPT_IMPORT_DIALECT_REASON = '台本のセリフを書き換えないため、方言は適用しません';

export const SCRIPT_IMPORT_MOTION_REASON = 'モーショングラフィックスで全編を作るときは台本から取り込めません';

/** 生成ペイロードに付ける imported_script (§6.2)。 */
export function buildImportedScriptPayload(script: ImportedScriptState): {
  text: string;
  filename: string;
  source_format: ImportedScriptState['sourceFormat'];
} {
  return { text: script.text, filename: script.filename, source_format: script.sourceFormat };
}

/**
 * 生成ペイロードに台本取り込みを反映する。読み込んでいなければ payload をそのまま返す。
 * 取り込み時は imported_script を付け、count=1 に固定し、dialect を送らない (BE も上書き・無視するが FE でも揃える)。
 */
export function applyScriptImportToPayload(
  payload: Record<string, unknown>,
  script: ImportedScriptState | null | undefined,
): Record<string, unknown> {
  if (!script) return payload;
  const { dialect: _dialect, ...rest } = payload;
  void _dialect;
  return { ...rest, count: 1, imported_script: buildImportedScriptPayload(script) };
}

// ── 生成後 (generation_metadata) の読み出し ──────────────────────────

type MetaLike = Record<string, unknown> | null | undefined;

export function readScenarioOrigin(meta: MetaLike): ScenarioOrigin {
  const v = meta?.origin;
  return v === 'imported' || v === 'blank' ? v : 'generated';
}

export function readImportedScriptMeta(meta: MetaLike): ImportedScriptMeta | null {
  const v = meta?.imported_script;
  return v && typeof v === 'object' ? (v as ImportedScriptMeta) : null;
}

export function readImportReport(meta: MetaLike): ImportReport | null {
  const v = meta?.import_report;
  return v && typeof v === 'object' ? (v as ImportReport) : null;
}

/**
 * API が返した import_report を既存の generation_metadata.import_report にマージする。
 * 応答は完全な import_report なので上書きでよいが、既存にあって応答に無いキー (一覧 API 由来の欠落等) は残す。
 */
export function mergeImportReportIntoMeta<M extends Record<string, unknown>>(
  meta: M | null | undefined,
  report: ImportReport,
): M & { import_report: ImportReport } {
  const base = (meta ?? {}) as M;
  const prev = readImportReport(base) ?? {};
  return { ...base, import_report: { ...prev, ...report } };
}

/** シナリオ配列のうち scenarioId のものだけ import_report をマージする (該当なしなら同じ配列を返す)。 */
export function mergeImportReportIntoScenarios<S extends { id: string; generation_metadata?: Record<string, unknown> | null }>(
  scenarios: S[],
  scenarioId: string,
  report: ImportReport,
): S[] {
  if (!scenarios.some((s) => s.id === scenarioId)) return scenarios;
  return scenarios.map((s) => (s.id !== scenarioId ? s : {
    ...s,
    generation_metadata: mergeImportReportIntoMeta(s.generation_metadata, report),
  }));
}

/** 台本取り込みのシナリオか (一覧バッジ・レポート表示の判定)。 */
export function isImportedScenario(meta: MetaLike): boolean {
  return readScenarioOrigin(meta) === 'imported';
}

/** 総合状態の表示ラベル (§4.3 / §6.6.7)。自動判定だけでは「検証済み」と表示しない。 */
export function importOverallLabel(overall: ImportVerification['overall'] | null | undefined): string {
  switch (overall) {
    case 'user_verified':
      return '検証済み（利用者確認）';
    case 'auto_checked':
      return '自動照合OK（原文の確認待ち）';
    case 'needs_review':
    default:
      return '要確認';
  }
}

/** 行の状態のうち赤字にするもの (§4.3)。 */
const ALERT_LINE_STATUSES = new Set(['mismatch', 'truncated', 'dropped', 'missing', 'unverified', 'extra']);
export function isAlertLineStatus(status: string | null | undefined): boolean {
  return !!status && ALERT_LINE_STATUSES.has(status);
}

export const IMPORT_LINE_STATUS_LABELS: Record<string, string> = {
  exact: '一致',
  split: '分割して一致',
  normalized: '表記ゆれ',
  truncated: '切り詰め',
  dropped: '行数超過で欠落',
  mismatch: '不一致',
  missing: '欠落',
  extra: '台本に無い行',
  unverified: '未確認',
};

export const IMPORT_LINE_KIND_LABELS: Record<string, string> = {
  dialogue: 'セリフ',
  narration: 'ナレーション',
  telop: 'テロップ',
};

/** 原文との参照照合の注記 (常に併記する。§4.3)。 */
export const SOURCE_MATCH_SCOPE_NOTE = '候補行の抽出規則に基づく参照確認であり、原文の網羅性・話者・種別・尺は自動では確認していません';
/** 自動照合の失効時の注記 (§6.6.7)。 */
export const IMPORT_STALE_NOTE = '取り込み後に編集あり。自動照合は取り込み時点のものです';

/** 照合の行番号表記 (BE の format_numbered_script と同じ `L001` 形式。桁数は総行数に合わせて最低3桁)。 */
export function formatScriptLineNumber(line: number, totalLines = 0): string {
  const width = Math.max(3, String(totalLines).length);
  return `L${String(line).padStart(width, '0')}`;
}
